import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, Plus } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  crearTarifa,
  listarTarifas,
  type TarifaCreatePayload,
  type TarifaRead,
} from "./api";

export function TarifasListPage(): JSX.Element {
  const [openForm, setOpenForm] = useState(false);

  const tarifasQ = useQuery({
    queryKey: ["tarifas"],
    queryFn: listarTarifas,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tarifas"
        icon={DollarSign}
        subtitle={
          <>
            Cada tarifa agrupa un set de cargos fijos (TarifaBase) y tramos por m³
            (TramoTarifa). Las vigencias no se editan: para cambiar valores se crea
            una nueva vigencia que cierra la anterior.
            {tarifasQ.data && <> ({tarifasQ.data.length} tarifas)</>}
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setOpenForm((v) => !v)}>
            Nueva tarifa
          </Button>
        }
      />

      {openForm && <CrearTarifaForm onClose={() => setOpenForm(false)} />}

      {tarifasQ.isPending && <Cargando />}
      {tarifasQ.isError && (
        <ErrorCarga
          error={tarifasQ.error}
          mensaje="Error al cargar tarifas."
          onReintentar={() => void tarifasQ.refetch()}
        />
      )}

      {tarifasQ.data && tarifasQ.data.length === 0 && (
        <p className="text-sm text-neutral-500">
          Sin tarifas configuradas. Crea la primera con el botón de arriba.
        </p>
      )}

      {tarifasQ.data && tarifasQ.data.length > 0 && (
        <TableWrap label="Tarifas">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Nombre</th>
                <th scope="col" className="px-3 py-2 text-right">% Alcantarillado</th>
                <th scope="col" className="px-3 py-2 text-right">{""}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {tarifasQ.data.map((t) => (
                <TarifaRow key={t.id} tarifa={t} />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function TarifaRow({ tarifa }: { tarifa: TarifaRead }): JSX.Element {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2 font-medium text-neutral-900">{tarifa.nombre}</td>
      <td className="px-3 py-2 text-right text-neutral-600">
        {(tarifa.porcentaje_alcantarillado * 100).toFixed(1)}%
      </td>
      <td className="px-3 py-2 text-right">
        <Link
          to={`/tarifas/${tarifa.id}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          Detalle
          <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
        </Link>
      </td>
    </tr>
  );
}

function CrearTarifaForm({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState("");
  const [porcentaje, setPorcentaje] = useState("0");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (p: TarifaCreatePayload) => crearTarifa(p),
    onSuccess: (t) => {
      toast.success(`Tarifa "${t.nombre}" creada`);
      void qc.invalidateQueries({ queryKey: ["tarifas"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");
    const p = Number(porcentaje);
    if (!Number.isFinite(p) || p < 0 || p > 1)
      return setError("Porcentaje debe estar entre 0 y 1");
    mutation.mutate({
      nombre: nombre.trim(),
      porcentaje_alcantarillado: p,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([nombre, porcentaje]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva tarifa"
      icon={DollarSign}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Creando…" : "Crear tarifa"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Nombre"
          type="text"
          maxLength={50}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="ej. Residencial"
          disabled={mutation.isPending}
          autoFocus
        />
        <Input
          label="% Alcantarillado (0.00 – 1.00)"
          type="number"
          step="0.01"
          min={0}
          max={1}
          value={porcentaje}
          onChange={(e) => setPorcentaje(e.target.value)}
          disabled={mutation.isPending}
        />
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}
